// AffiliatePayoutsAdmin.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Dropdown, Form, Pagination, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {fetchAffiliatePayoutsAdmin, payAffiliatePayoutAdmin} from "../../http/userAPI";
import Modal from "react-bootstrap/Modal"; // проверь путь

const ORDER_STATUSES = ["pending", "approved", "planed", "rejected", "paid"];
const PAYMENT_STATUSES = ["pending", "completed", "failed"];

const AffiliatePayoutsAdmin = () => {
    // ===== Фильтры =====
    const [q, setQ] = useState("");
    const [orderStatuses, setOrderStatuses] = useState([]);     // multi
    const [payStatuses, setPayStatuses] = useState([]);         // multi
    const [requestedFrom, setRequestedFrom] = useState(null);   // Date | null
    const [requestedTo, setRequestedTo]     = useState(null);
    const [paymentFrom, setPaymentFrom]     = useState(null);
    const [paymentTo, setPaymentTo]         = useState(null);

    // ===== Пагинация =====
    const [page, setPage]   = useState(1);
    const [limit, setLimit] = useState(50);
    const [count, setCount] = useState(0);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(count / Math.max(1, limit))), [count, limit]);

    // ===== Данные =====
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // пропустить автозагрузку один раз (после Reset)
    const skipNextLoadRef = useRef(true);

    // ===== Pay Modal =====
    const [payOpen, setPayOpen] = useState(false);
    const [paySubmitting, setPaySubmitting] = useState(false);
    const [payTarget, setPayTarget] = useState(null); // текущая заявка
    const [payForm, setPayForm] = useState({
        payment_method: "",
        payment_transaction_id: "",
        payment_fee: "",
        admin_comment: "",
        payment_date: "", // yyyy-mm-dd
    });

    const payable = useCallback(
        (o) => ["pending", "approved", "planed"].includes(String(o?.order_status || "")),
        []
    );

    const openPay = useCallback((o) => {
        setPayTarget(o);
        setPayForm({
            payment_method: o?.payment_method || "",
            payment_transaction_id: "",
            payment_fee: "",
            admin_comment: "",
            payment_date: new Date().toISOString().slice(0, 10),
        });
        setPayOpen(true);
    }, []);

    const closePay = useCallback(() => {
        if (!paySubmitting) setPayOpen(false);
    }, [paySubmitting]);

    // ===== Хелперы =====
    const toMinutesStartOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d); x.setHours(0,0,0,0);
        return Math.floor(x.getTime() / 60000);
    };
    const toMinutesEndOfDay = (d) => {
        if (!d) return undefined;
        const x = new Date(d); x.setHours(23,59,59,999);
        return Math.floor(x.getTime() / 60000);
    };
    const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");
    const fmtMoney2   = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "—");

    const toggleFromList = useCallback((val, list, setter) => {
        setter(list.includes(val) ? list.filter((s) => s !== val) : [...list, val]);
    }, []);
    const clearOrderStatuses = useCallback(() => setOrderStatuses([]), []);
    const clearPayStatuses   = useCallback(() => setPayStatuses([]), []);

    // ===== Параметры запроса
    const buildQuery = useCallback(() => {
        const base = {
            page,
            limit,
            q: q?.trim() || undefined,
            statuses: orderStatuses.length ? orderStatuses : undefined,
            pstatuses: payStatuses.length ? payStatuses : undefined,
            requested_from: toMinutesStartOfDay(requestedFrom),
            requested_to:   toMinutesEndOfDay(requestedTo),
            payment_from:   toMinutesStartOfDay(paymentFrom),
            payment_to:     toMinutesEndOfDay(paymentTo),
        };
        return Object.fromEntries(Object.entries(base).filter(([,v]) => v !== undefined && v !== ""));
    }, [page, limit, q, orderStatuses, payStatuses, requestedFrom, requestedTo, paymentFrom, paymentTo]);

    // ===== Загрузка
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await fetchAffiliatePayoutsAdmin(buildQuery());
            const list = payload?.items || [];
            setItems(list);
            setCount(Number(payload?.count || 0));
            setMessage(list.length ? "" : "No payouts found");
        } catch (e) {
            console.error("fetchAffiliatePayoutsAdmin failed:", e);
            setItems([]);
            setCount(0);
            setMessage("Failed to fetch payouts");
            toast.error("Error fetching payouts", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    const submitPay = useCallback(async () => {
        if (!payTarget?.id) return;
        setPaySubmitting(true);
        try {
            const payload = {
                payment_method: payForm.payment_method?.trim() || undefined,
                payment_transaction_id: payForm.payment_transaction_id?.trim() || undefined,
                payment_fee: payForm.payment_fee !== "" ? Number(payForm.payment_fee) : undefined,
                admin_comment: payForm.admin_comment?.trim() || undefined,
                payment_date: payForm.payment_date ? new Date(payForm.payment_date).toISOString() : undefined,
            };
            await payAffiliatePayoutAdmin(payTarget.id, payload);
            if (typeof toast !== "undefined") {
                toast.success(`Paid #${payTarget.id}`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
            setPayOpen(false);
            setPayTarget(null);
            await loadData();
        } catch (e) {
            console.error(e);
            if (typeof toast !== "undefined") {
                toast.error(e?.message || "Payment failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            }
        } finally {
            setPaySubmitting(false);
        }
    }, [payTarget, payForm, loadData]);


    // ===== Действия
    const applyFilter = useCallback(async () => {
        setPage(1);
        setLoading(true);
        try {
            const payload = await fetchAffiliatePayoutsAdmin({ ...buildQuery(), page: 1 });
            const list = payload?.items || [];
            setItems(list);
            setCount(Number(payload?.count || 0));
            setMessage(list.length ? "" : "No payouts found");
        } catch (e) {
            console.error(e);
            toast.error("Failed to apply filter", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    // Reset — без запроса
    const resetFilters = useCallback(() => {
        skipNextLoadRef.current = true;
        setQ("");
        setOrderStatuses([]);
        setPayStatuses([]);
        setRequestedFrom(null);
        setRequestedTo(null);
        setPaymentFrom(null);
        setPaymentTo(null);
        setPage(1);
        setItems([]);
        setCount(0);
        setMessage("");
        setLoading(false);
    }, []);

    // Пагинация
    const handlePageChange = useCallback((next) => {
        const p = Math.max(1, Math.min(next, totalPages || 1));
        if (p !== page) setPage(p);
    }, [page, totalPages]);
    const handleLimitChange = useCallback((val) => {
        const l = Math.max(1, Math.min(val || 50, 200));
        if (l !== limit) { setLimit(l); setPage(1); }
    }, [limit]);

    // Автозагрузка при изменении page/limit
    useEffect(() => {
        if (skipNextLoadRef.current) { skipNextLoadRef.current = false; return; }
        loadData();
    }, [page, limit, loadData]);

    // ===== РЕНДЕР =====
    return (
        <>
            <Card className="shadow-sm bg-light p-0">
                <Card.Body>
                    {/* Фильтры в одну строку */}
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
                        {/* Order Status (multi) */}
                        <div>
                            <small className="text-muted d-none d-block mb-1">Order Status</small>
                            <Dropdown>
                                <Dropdown.Toggle variant="secondary" size="sm" style={{ minWidth: 140 }}>
                                    {orderStatuses.length ? `Selected: ${orderStatuses.length}` : "Order Status"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ minWidth: 220 }}>
                                    <Dropdown.Item onClick={clearOrderStatuses} active={orderStatuses.length === 0}>— Any —</Dropdown.Item>
                                    <Dropdown.Divider />
                                    {ORDER_STATUSES.map((s) => (
                                        <Dropdown.Item
                                            as="button"
                                            key={s}
                                            onClick={() => toggleFromList(s, orderStatuses, setOrderStatuses)}
                                            active={orderStatuses.includes(s)}
                                        >
                                            <Form.Check type="checkbox" className="me-2 d-inline-block" checked={orderStatuses.includes(s)} readOnly /> {s}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Payment Status (multi) */}
                        <div>
                            <small className="text-muted d-none d-block mb-1">Payment Status</small>
                            <Dropdown>
                                <Dropdown.Toggle variant="secondary" size="sm" style={{ minWidth: 140 }}>
                                    {payStatuses.length ? `Selected: ${payStatuses.length}` : "Payment Status"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ minWidth: 220 }}>
                                    <Dropdown.Item onClick={clearPayStatuses} active={payStatuses.length === 0}>— Any —</Dropdown.Item>
                                    <Dropdown.Divider />
                                    {PAYMENT_STATUSES.map((s) => (
                                        <Dropdown.Item
                                            as="button"
                                            key={s}
                                            onClick={() => toggleFromList(s, payStatuses, setPayStatuses)}
                                            active={payStatuses.includes(s)}
                                        >
                                            <Form.Check type="checkbox" className="me-2 d-inline-block" checked={payStatuses.includes(s)} readOnly /> {s}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <Button variant="success" size="sm" onClick={applyFilter} disabled={loading}>
                            {loading ? (<><Spinner size="sm" className="me-1" />Apply…</>) : "Apply Filter"}
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={resetFilters} disabled={loading}>
                            Reset
                        </Button>

                        {/* Поиск */}
                        <div style={{ width: 200 }} className="d-none d-md-block">
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Search…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </div>



                        {/* Requested + Payment ranges — скрыто на моб, в одну строку на десктопе */}
                        <div className="d-none d-md-flex flex-nowrap align-items-end gap-3">
                            {/* Requested range */}
                            <div className="d-flex align-items-end gap-1">
                                <div>
                                    <small className="text-muted d-block mb-1">Requested</small>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={requestedFrom ? new Date(requestedFrom).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setRequestedFrom(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={requestedTo ? new Date(requestedTo).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setRequestedTo(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                            </div>

                            {/* Payment range */}
                            <div className="d-flex align-items-end gap-1">
                                <div>
                                    <small className="text-muted d-block mb-1">Payment</small>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={paymentFrom ? new Date(paymentFrom).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setPaymentFrom(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                                <div>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        value={paymentTo ? new Date(paymentTo).toISOString().slice(0, 10) : ""}
                                        onChange={(e) => setPaymentTo(e.target.value ? new Date(e.target.value) : null)}
                                        style={{ width: 110 }}
                                    />
                                </div>
                            </div>
                        </div>


                        {/* Per Page (PP) */}
                        <div className="ms-3">
                            <Form.Select
                                size="sm"
                                value={limit}
                                onChange={(e) => handleLimitChange(Number(e.target.value))}
                                style={{ width: 60 }}
                            >
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                            </Form.Select>
                        </div>
                    </div>

                    {/* Таблица */}
                    {message && <p className="text-muted mt-2 text-center">{message}</p>}

                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" /> <small>Loading…</small>
                        </div>
                    ) : items.length > 0 ? (
                        <>
                            <div style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 10 }}>
                                <Table striped bordered hover size="sm" className="mb-2 align-middle" style={{ minWidth: 1250 }}>
                                    <thead className="small">
                                    <tr className="small">
                                        <th>ID</th>
                                        <th>Order Status</th>
                                        <th>Requested</th>
                                        <th>Payment Date</th>
                                        <th>UserID</th>
                                        <th>User Name</th>
                                        <th>AMZ Name</th>
                                        <th className="text-end">Req, $</th>
                                        <th className="text-end">Aff Before</th>
                                        <th className="text-end">Aff After</th>
                                        <th>Pay Method</th>
                                        <th>Pay Status</th>
                                        <th>Txn ID</th>
                                        <th className="text-end">Fee, $</th>
                                        <th>Comment</th>
                                        <th>Admin Note</th>
                                        <th>To E-Mail</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody className="small">
                                    {items.map((o) => (
                                        <tr key={o.id} className="align-middle small">
                                            <td className="py-0 px-2">{o.id}</td>
                                            <td className="py-0 px-2">
                                                <Badge bg={
                                                    o.order_status === "approved" ? "secondary" :
                                                        o.order_status === "planed"   ? "warning" :
                                                            o.order_status === "paid"     ? "success" :
                                                                o.order_status === "rejected" ? "danger"  : "light"
                                                } text={o.order_status === "pending" ? "dark" : undefined}>
                                                    {o.order_status}
                                                </Badge>
                                            </td>
                                            <td className="py-0 px-2">{fmtDateTime(o.requested_date)}</td>
                                            <td className="py-0 px-2">{o.payment_date ? fmtDateTime(o.payment_date) : "—"}</td>
                                            <td className="py-0 px-2">{o.user_id}</td>
                                            <td className="py-0 px-2">{o.user_name || "—"}</td>
                                            <td className="py-0 px-2">{o.user_amz_name || "—"}</td>
                                            <td className="py-0 px-2 text-end">{fmtMoney2(o.requested_amount)}</td>
                                            <td className="py-0 px-2 text-end">{fmtMoney2(o.affiliate_balance_before)}</td>
                                            <td className="py-0 px-2 text-end">{fmtMoney2(o.affiliate_balance_after)}</td>
                                            <td className="py-0 px-2">{o.payment_method || "—"}</td>
                                            <td className="py-0 px-2">
                                                <Badge bg={
                                                    o.payment_status === "completed" ? "success" :
                                                        o.payment_status === "failed"    ? "danger"  : "secondary"
                                                }>
                                                    {o.payment_status}
                                                </Badge>
                                            </td>
                                            <td className="py-0 px-2">{o.payment_transaction_id || "—"}</td>
                                            <td className="py-0 px-2 text-end">{fmtMoney2(o.payment_fee)}</td>
                                            <td className="py-0 px-2">{o.order_comment || "—"}</td>
                                            <td className="py-0 px-2">{o.admin_comment || "—"}</td>
                                            <td className="py-0 px-2">{o.payment_details || "—"}</td>
                                            <td className="py-1 px-2">
                                                <div className="d-flex gap-0">
                                                    <Button
                                                        size="sm"
                                                        className="btn btn-sm"
                                                        variant="success"
                                                        disabled={!payable(o)}
                                                        onClick={() => openPay(o)}
                                                        title={payable(o) ? "Pay this request" : "Not payable in current status"}
                                                    >
                                                        Pay
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Пагинация снизу */}
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="small text-muted">
                                    Total: {count} • Page {page} of {totalPages}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page <= 1} />
                                    <Pagination.Item active>{page}</Pagination.Item>
                                    <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} />
                                </Pagination>
                            </div>
                        </>
                    ) : null}
                </Card.Body>
            </Card>
            <Modal show={payOpen} onHide={closePay} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Pay Affiliate Payout {payTarget ? `#${payTarget.id}` : ""}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-2">
                        <Form.Group>
                            <Form.Label className="small mb-1">Payment Method</Form.Label>
                            <Form.Control
                                size="sm"
                                value={payForm.payment_method}
                                onChange={(e)=>setPayForm(f=>({ ...f, payment_method: e.target.value }))}
                                placeholder="PayPal / Bank / Crypto"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="small mb-1">Transaction ID</Form.Label>
                            <Form.Control
                                size="sm"
                                value={payForm.payment_transaction_id}
                                onChange={(e)=>setPayForm(f=>({ ...f, payment_transaction_id: e.target.value }))}
                                placeholder="PP-12345 / hash"
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Form.Group className="flex-fill">
                                <Form.Label className="small mb-1">Fee, $</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    value={payForm.payment_fee}
                                    onChange={(e)=>setPayForm(f=>({ ...f, payment_fee: e.target.value }))}
                                />
                            </Form.Group>
                            <Form.Group style={{width: 160}}>
                                <Form.Label className="small mb-1">Payment Date</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="date"
                                    value={payForm.payment_date}
                                    onChange={(e)=>setPayForm(f=>({ ...f, payment_date: e.target.value }))}
                                />
                            </Form.Group>
                        </div>
                        <Form.Group>
                            <Form.Label className="small mb-1">Admin Note</Form.Label>
                            <Form.Control
                                size="sm"
                                as="textarea"
                                rows={2}
                                value={payForm.admin_comment}
                                onChange={(e)=>setPayForm(f=>({ ...f, admin_comment: e.target.value }))}
                            />
                        </Form.Group>
                        {payTarget && (
                            <small className="text-muted">
                                Request: <b>${Number(payTarget.requested_amount||0).toFixed(2)}</b> • User #{payTarget.user_id}
                            </small>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={closePay} disabled={paySubmitting}>Cancel</Button>
                    <Button variant="success" size="sm" onClick={submitPay} disabled={paySubmitting}>
                        {paySubmitting ? <>Processing…</> : <>Pay</>}
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default AffiliatePayoutsAdmin;
